import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ConfigProvider, theme as antdTheme } from "antd";
import { setAccentMode, setSurfaceMode } from "./palette";
import { useFontMode, FONT_STACK_DENSE, FONT_STACK_LIGHT } from "../utils/fontMode";

const PALETTE = {
  primary: "#6366f1",
  info: "#6366f1",
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
  darkLayout: "#0a0b0f",
  darkSurface: "#0e1015",
  darkElevated: "#12141b",
  darkText: "#e2e4e9",
  darkTextSecondary: "#8d93a3",
  lightLayout: "#f5f6f8",
  lightSurface: "#ffffff",
  lightElevated: "#ffffff",
  lightText: "#0e1015",
  lightTextSecondary: "#3f4450",
  // "Graphite" — the light theme structure (crisp surfaces, simple shadows,
  // no glassmorphism) inverted to a strict charcoal palette. White becomes
  // dark grey, layout becomes near-black; everything stays monochrome.
  graphiteLayout: "#1c1f23",
  graphiteSurface: "#262a30",
  graphiteElevated: "#2f3339",
  graphiteText: "#f0f1f4",
  graphiteTextSecondary: "#c6c9d1",
  // «Мягко красный» — тёмная тема с красным акцентом вместо индиго. Поверхности
  // те же, что у dark; отличается только основной цвет. Он приглушённый, а не
  // сигнальный, чтобы не спорить с красным ошибок и удаления.
  crimsonPrimary: "#d65f5f",
  // «Зелёная» — та же тёмная тема с мягким зелёным акцентом.
  greenPrimary: "#3a8f69",
  // «Белая» — та же тёмная тема с белым акцентом.
  whitePrimary: "#eceef2",
  // «Бордовая» — та же тёмная тема с тёмно-бордовым акцентом.
  maroonPrimary: "#660000"
};

const FONT_STACK = "Sora, Manrope, Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Centralized AntD 5 design tokens.
const SHARED_TOKENS = {
  colorPrimary: PALETTE.primary,
  colorInfo: PALETTE.info,
  colorSuccess: PALETTE.success,
  colorWarning: PALETTE.warning,
  colorError: PALETTE.error,
  borderRadius: 16,
  borderRadiusSM: 12,
  borderRadiusLG: 20,
  controlHeight: 40,
  controlHeightSM: 36,
  controlHeightLG: 44,
  fontFamily: FONT_STACK
};

const DARK_TOKENS = {
  ...SHARED_TOKENS,
  colorBgLayout: PALETTE.darkLayout,
  colorBgContainer: PALETTE.darkSurface,
  colorBgElevated: PALETTE.darkElevated,
  colorText: PALETTE.darkText,
  colorTextBase: PALETTE.darkText,
  colorTextSecondary: PALETTE.darkTextSecondary
};

const LIGHT_TOKENS = {
  ...SHARED_TOKENS,
  colorBgLayout: PALETTE.lightLayout,
  colorBgContainer: PALETTE.lightSurface,
  colorBgElevated: PALETTE.lightElevated,
  colorText: PALETTE.lightText,
  colorTextBase: PALETTE.lightText,
  colorTextSecondary: PALETTE.lightTextSecondary
};

// "Graphite" — third selectable mode. Same crisp light-theme structure
// (simple shadows, no blur), but every white surface is swapped for a deep
// charcoal grey. Reads as "dark version of the light theme" rather than a
// colored dark theme; primary stays indigo for accent consistency.
const GRAPHITE_TOKENS = {
  ...SHARED_TOKENS,
  colorBgLayout: PALETTE.graphiteLayout,
  colorBgContainer: PALETTE.graphiteSurface,
  colorBgElevated: PALETTE.graphiteElevated,
  colorText: PALETTE.graphiteText,
  colorTextBase: PALETTE.graphiteText,
  colorTextSecondary: PALETTE.graphiteTextSecondary
};

// AntD выводит из colorPrimary свои оттенки (наведение, нажатие, рамки), и
// посчитать их по var() он не может — поэтому цвет здесь задан значением, тем же
// самым, что стоит в --accent-500 у этой темы в index.css.
const CRIMSON_TOKENS = {
  ...DARK_TOKENS,
  colorPrimary: PALETTE.crimsonPrimary,
  colorInfo: PALETTE.crimsonPrimary
};

const GREEN_TOKENS = {
  ...DARK_TOKENS,
  colorPrimary: PALETTE.greenPrimary,
  colorInfo: PALETTE.greenPrimary
};

// На белом акценте белый текст не читается — у AntD он задан colorTextLightSolid.
const WHITE_TOKENS = {
  ...DARK_TOKENS,
  colorPrimary: PALETTE.whitePrimary,
  colorInfo: PALETTE.whitePrimary,
  colorTextLightSolid: "#0e1015"
};

const MAROON_TOKENS = {
  ...DARK_TOKENS,
  colorPrimary: PALETTE.maroonPrimary,
  colorInfo: PALETTE.maroonPrimary
};

const COMPONENT_TOKENS = {
  Button: {
    borderRadius: SHARED_TOKENS.borderRadius,
    controlHeight: SHARED_TOKENS.controlHeight,
    fontWeight: 600
  },
  Input: {
    borderRadius: SHARED_TOKENS.borderRadius
  },
  Select: {
    borderRadius: SHARED_TOKENS.borderRadius
  },
  Card: {
    borderRadiusLG: SHARED_TOKENS.borderRadiusLG
  },
  Modal: {
    borderRadiusLG: SHARED_TOKENS.borderRadiusLG
  }
};

const STORAGE_KEY = "kl-theme-mode";

// Recognised theme modes. `toggleMode` cycles through this order, and any
// stored value outside the list falls back to "dark". Legacy "deep-purple"
// from earlier builds is silently remapped to "graphite".
const SUPPORTED_MODES = ["dark", "light", "graphite", "maroon", "crimson", "green", "white"];

// Темы, которые рисуются тёмными: все, кроме светлой.
export const isDarkThemeMode = (mode) => mode !== "light";
const isSupportedMode = (value) => SUPPORTED_MODES.includes(value);
const normalizeMode = (value) => {
  if (value === "deep-purple") return "graphite";
  return isSupportedMode(value) ? value : null;
};

const detectInitialTheme = () => {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const normalized = normalizeMode(stored);
    if (normalized) return normalized;
  } catch (_) {
    // localStorage might be disabled (Safari private mode); fall through.
  }
  // Default: keep current dark look unless the OS strongly prefers light.
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
};

const ThemeContext = createContext({
  mode: "dark",
  setMode: () => {},
  toggleMode: () => {},
  availableModes: SUPPORTED_MODES
});

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(detectInitialTheme);

  // Ставится во время отрисовки, а не в эффекте: дети рисуются следом и должны
  // увидеть уже новый акцент. Из эффекта они успели бы отрисоваться со старым и
  // получили бы лишний проход.
  setAccentMode(mode);
  setSurfaceMode(mode);

  // Keep <html> annotated so plain CSS (and Tailwind's `dark:` variant) can
  // react to the active theme without prop-drilling.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.setAttribute("data-theme", mode);
    // Tailwind's `dark:` variant only flips on the `.dark` class. Both "dark"
    // and the new "graphite" modes are visually dark, so we keep the class
    // set for both — bespoke styling differentiates via the `data-theme`
    // attribute.
    const isDarkVariant = isDarkThemeMode(mode);
    if (isDarkVariant) {
      root.classList.add("dark");
      root.classList.remove("light");
    } else {
      root.classList.add("light");
      root.classList.remove("dark");
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (_) {
      // ignore persistence failures
    }
  }, [mode]);

  const setMode = useCallback((next) => {
    setModeState(normalizeMode(next) || "dark");
  }, []);

  // Cycle dark → light → graphite → dark. Existing call-sites that used
  // `toggleMode` to flip between dark/light keep working — they just visit
  // the third stop on every other tap.
  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const idx = SUPPORTED_MODES.indexOf(prev);
      const next = SUPPORTED_MODES[(idx + 1) % SUPPORTED_MODES.length];
      return next || "dark";
    });
  }, []);

  const fontMode = useFontMode();
  const antdConfig = useMemo(() => {
    const isDarkAlgo = isDarkThemeMode(mode);
    let token = DARK_TOKENS;
    if (mode === "light") token = LIGHT_TOKENS;
    else if (mode === "graphite") token = GRAPHITE_TOKENS;
    else if (mode === "crimson") token = CRIMSON_TOKENS;
    else if (mode === "green") token = GREEN_TOKENS;
    else if (mode === "white") token = WHITE_TOKENS;
    else if (mode === "maroon") token = MAROON_TOKENS;
    return {
      algorithm: isDarkAlgo ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: { ...token, fontFamily: fontMode === "light" ? FONT_STACK_LIGHT : FONT_STACK_DENSE },
      components: COMPONENT_TOKENS,
      cssVar: true,
      hashed: false
    };
  }, [mode, fontMode]);

  const ctxValue = useMemo(
    () => ({ mode, setMode, toggleMode, availableModes: SUPPORTED_MODES }),
    [mode, setMode, toggleMode]
  );

  return (
    <ThemeContext.Provider value={ctxValue}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
